<template>
  <div class="email-input-container">
    <div
      class="recipient-listbox"
      tabindex="-1"
      role="listbox"
      aria-multiselectable="true"
      aria-orientation="horizontal"
      aria-label="Email recipient search"
    >
      <div
        class="input-wrapper"
        style="height: 36px; flex-basis: 48px;"
        role="presentation"
      >
        <div class="email-chips">
          <input
            id="recipient-input"
            :value="inputValue"
            class="recipient-input-field"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            aria-label="To recipients"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded="false"
            :placeholder="$t('INBOX_MGMT.ADD.MICROSOFT.EMAIL_PLACEHOLDER')"
            type="text"
            role="combobox"
            aria-owns="recipient-list"
            aria-controls="recipient-list"
            aria-description="Enter emails separated by commas or spaces"
            @input="handleInput"
            @paste="handlePaste"
            @keydown="handleKeydown"
            @focus="handleFocus"
            @blur="handleBlur"
          />
          <span class="input-suffix" />
          <span
            v-for="(email, index) in emails"
            :key="index"
            class="email-chip"
          >
            {{ email }}
            <button
              type="button"
              class="remove-chip"
              @click.stop="() => removeEmail(index)"
            >
              <fluent-icon
                icon="add"
                size="14"
                style="transform: rotate(45deg)"
              />
            </button>
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="showSuggestions && inputValue.length >= 2"
      ref="suggestionList"
      class="suggestions-popup"
    >
      <div v-if="isLoading" class="container">
        <div class="post">
          <div class="line" />
          <div class="line" />
        </div>
      </div>
      <div v-else id="recipient-list" aria-label="Suggested contacts">
        <div v-if="hasMultipleEmails">
          <div
            v-for="(emailObj, index) in parsedEmails"
            :key="`email-${index}`"
            class="suggestion-item"
            :class="{
              'invalid-email': !emailObj.isValid,
              'valid-email': emailObj.isValid,
            }"
            @click="emailObj.isValid ? selectContact(emailObj.email) : null"
            @mousedown.prevent
          >
            <span class="contact-email" :title="emailObj.email">
              {{ emailObj.email }}
              <span v-if="!emailObj.isValid" class="error-text">
                {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.INVALID_EMAIL') }}
              </span>
            </span>
          </div>
        </div>
        <div
          v-else-if="!filteredContacts.length && isValidEmail(inputValue)"
          class="suggestion-item"
          @click="selectContact(inputValue)"
          @mousedown.prevent
        >
          <span class="contact-email" :title="inputValue">
            {{ inputValue }}
          </span>
        </div>
        <div v-else-if="isInvalidEmail" class="suggestion-item">
          <span class="contact-email">
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR') }}
          </span>
        </div>
        <div
          v-for="(contact, index) in filteredContacts"
          :key="`contact-${index}`"
          class="suggestion-item"
          :aria-selected="selectedSuggestionIndex === index"
          :class="{ hovered: selectedSuggestionIndex === index }"
          @click="selectContact(contact.email)"
          @mousedown.prevent
          @mouseenter="selectedSuggestionIndex = index"
        >
          <span class="contact-name" :title="contact.name">
            {{ contact.name }},
          </span>
          <span class="contact-email" :title="contact.email">{{
            contact.email
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiEmailInput',
  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      emails: [],
      showSuggestions: false,
      selectedSuggestionIndex: -1,
    };
  },
  computed: {
    hasMultipleEmails() {
      return this.inputValue.match(/[, ]+/);
    },
    parsedEmails() {
      if (!this.hasMultipleEmails) return [];
      return this.inputValue
        .split(/[, ]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0)
        .map(email => ({
          email,
          isValid: this.isValidEmail(email),
        }));
    },
    filteredContacts() {
      if (!this.inputValue) return this.contacts;
      const searchTerm = (this.hasMultipleEmails
        ? this.inputValue
            .split(/[, ]+/)
            .pop()
            .trim()
        : this.inputValue
      ).toLowerCase();

      return this.contacts.filter(
        contact =>
          contact.name.toLowerCase().includes(searchTerm) ||
          contact.email.toLowerCase().includes(searchTerm)
      );
    },
    isInvalidEmail() {
      return (
        !this.hasMultipleEmails &&
        this.inputValue.length >= 2 &&
        !this.isValidEmail(this.inputValue) &&
        this.filteredContacts.length === 0
      );
    },
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    handleInput(e) {
      this.inputValue = e.target.value;
      this.showSuggestions = this.inputValue.length >= 2;
      this.selectedSuggestionIndex = -1;
      const searchTerm = this.hasMultipleEmails
        ? this.inputValue
            .split(/[, ]+/)
            .pop()
            .trim()
        : this.inputValue;
      this.$emit('on-change', searchTerm);
    },
    handlePaste(e) {
      e.preventDefault();
      const pastedText = (e.clipboardData || window.clipboardData).getData(
        'text'
      );
      const emails = pastedText
        .split(/[, ]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      const validEmails = emails.filter(
        email => this.isValidEmail(email) && !this.emails.includes(email)
      );

      if (validEmails.length > 0) {
        this.emails.unshift(...validEmails);
        this.$emit('update-emails', this.emails);
      }

      const invalidEmails = emails.filter(email => !this.isValidEmail(email));
      this.inputValue =
        invalidEmails.join(', ') + (invalidEmails.length ? ', ' : '');
      this.showSuggestions =
        this.inputValue.length >= 2 || validEmails.length > 0;
      this.selectedSuggestionIndex = -1;

      const searchTerm = invalidEmails.length
        ? invalidEmails[invalidEmails.length - 1]
        : '';
      this.$emit('on-change', searchTerm);
    },
    handleKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (this.selectedSuggestionIndex >= 0) {
          this.selectContact(
            this.filteredContacts[this.selectedSuggestionIndex].email
          );
        } else if (this.hasMultipleEmails) {
          const lastEmail = this.parsedEmails[this.parsedEmails.length - 1];
          if (lastEmail.isValid) {
            this.selectContact(lastEmail.email);
          }
        } else if (this.isValidEmail(this.inputValue)) {
          this.selectContact(this.inputValue);
        }
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (this.selectedSuggestionIndex < this.filteredContacts.length - 1) {
          this.selectedSuggestionIndex += 1;
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (this.selectedSuggestionIndex > -1) {
          this.selectedSuggestionIndex -= 1;
        }
      } else if (event.key === 'Escape') {
        this.showSuggestions = false;
        this.selectedSuggestionIndex = -1;
      } else if (event.key === ',' || event.key === ' ') {
        this.addEmails();
      }
    },
    handleFocus() {
      if (this.inputValue.length >= 2) {
        this.showSuggestions = true;
      }
    },
    handleBlur() {
      setTimeout(() => {
        this.showSuggestions = false;
        this.selectedSuggestionIndex = -1;
      }, 300);
    },
    addEmails() {
      if (!this.inputValue) return;

      const newEmails = this.parsedEmails
        .filter(
          emailObj => emailObj.isValid && !this.emails.includes(emailObj.email)
        )
        .map(emailObj => emailObj.email);

      if (newEmails.length > 0) {
        this.emails.unshift(...newEmails);
        this.$emit('update-emails', this.emails);
        this.inputValue =
          this.parsedEmails
            .filter(emailObj => !emailObj.isValid)
            .map(emailObj => emailObj.email)
            .join(', ') +
          (this.parsedEmails.some(emailObj => !emailObj.isValid) ? ', ' : '');
      }
    },
    removeEmail(index) {
      this.emails.splice(index, 1);
      this.$emit('update-emails', this.emails);
    },
    selectContact(email) {
      if (!this.isValidEmail(email) || this.emails.includes(email)) return;

      this.emails.unshift(email);
      this.$emit('update-emails', this.emails);
      if (this.hasMultipleEmails) {
        const emails = this.inputValue
          .split(/[, ]+/)
          .map(e => e.trim())
          .filter(e => e.length > 0 && e !== email);
        this.inputValue = emails.join(', ') + (emails.length ? ', ' : '');
      } else {
        this.inputValue = '';
      }

      this.$nextTick(() => {
        this.$el.querySelector('#recipient-input').focus();
      });

      this.showSuggestions = false;
      this.selectedSuggestionIndex = -1;
    },
  },
};
</script>

<style lang="scss" scoped>
$colors: (
  chip-bg: var(--lv-50),
  remove-hover: var(--r-900),
  container-border: var(--s-100),
);

$dimensions: (
  input-flex-basis: 48px,
  input-min-width: 100px,
  spacing-sm: 5px,
  spacing-xs: 2px,
  font-size-base: 14px,
  font-size-remove: 16px,
  border-radius: 4px,
);
$base-color: #ddd;
$shine-color: #e8e8e8;
$animation-duration: 1.6s;
$avatar-offset: 52 + 16;

@mixin background-gradient {
  background-image: linear-gradient(
    90deg,
    $base-color 0px,
    $shine-color 40px,
    $base-color 80px
  );
  background-size: 600px;
}
.w-hide {
  display: none !important;
}
.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.valid-email {
  cursor: pointer;
}
.valid-email:hover {
  background-color: #f0f0f0;
}
.invalid-email {
  color: map-get($colors, remove-hover);
  cursor: default;
}

.post {
  width: 220px;
  height: 80px;

  .line {
    float: left;
    width: 220px;
    height: 16px;
    margin-top: 12px;
    border-radius: 7px;

    @include background-gradient;
    animation: shine-lines $animation-duration infinite linear;
  }

  .line ~ .line {
    background-color: $base-color;
  }
}

@keyframes shine-lines {
  0% {
    background-position: -100px;
  }

  40%,
  100% {
    background-position: 140px;
  }
}

@keyframes shine-avatar {
  0% {
    background-position: -100px + $avatar-offset;
  }

  40%,
  100% {
    background-position: 140px + $avatar-offset;
  }
}

.hovered {
  background-color: var(--s-25);
}

.email-input-container {
  min-height: map-get($dimensions, container-min-height);
  border: 1px solid map-get($colors, container-border);
  border-radius: map-get($dimensions, border-radius);
  overflow-y: auto;
  padding: 5px;
  padding-top: 0;
  .suggestions-popup {
    position: absolute;
    padding: 16px 12px;
    background: white;
    top: 255px;
    left: 55px;
    z-index: 999;
    border: 1px solid var(--d-300);
    background: var(--white);
    box-shadow: 0 4px 12px -4px rgba(32, 31, 42, 0.1);
    border-radius: 16px;
    max-height: 300px;
    overflow: auto;
    .suggestion-item {
      max-width: 300px;
      word-break: break-word;
      padding: 8px;
      &:hover {
        background-color: var(--s-25);
        cursor: pointer;
      }
    }
  }
}

// Recipient listbox styles
.recipient-listbox {
  width: 100%;
  height: 86px;
  max-height: 100px;
  .input-wrapper {
    .email-chips {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      align-items: flex-start;
      gap: map-get($dimensions, spacing-sm);

      .email-chip {
        display: inline-flex;
        align-items: center;
        background-color: map-get($colors, chip-bg);
        border-radius: map-get($dimensions, border-radius);
        padding: map-get($dimensions, spacing-sm) 5px;
        margin: map-get($dimensions, spacing-xs);
        font-size: map-get($dimensions, font-size-base);

        .remove-chip {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: map-get($dimensions, spacing-sm);
          background: none;
          border: none;
          cursor: pointer;
          font-size: map-get($dimensions, font-size-remove);
          line-height: 1;
          transition: color 0.2s;
          width: 16px;
          height: 16px;

          &:hover {
            border-radius: 50%;
            background: var(--v-50);
            color: map-get($colors, remove-hover);
          }
        }
      }

      .recipient-input-field {
        margin: 0 !important;
        border: none;
        outline: none;
        flex-grow: 1;
        font-size: map-get($dimensions, font-size-base);
        padding: map-get($dimensions, spacing-sm);
        min-width: map-get($dimensions, input-min-width);
      }
    }
  }
}
</style>
